import React, { useState, useRef, useMemo } from 'react';
import './EducationModal.scss';
import useAnimatedModalClose from './useAnimatedModalClose';
import useModalFocusTrap from './useModalFocusTrap';
import useModalTouchSwipe from './useModalTouchSwipe';

const FOCUS_OPTIONS = [
  { value: 'Education & Graduation Support', label: 'EDUCATION & GRADUATION SUPPORT' },
  { value: 'Emergency Management', label: 'EMERGENCY MANAGEMENT' },
  { value: 'Engineering', label: 'ENGINEERING' },
  { value: 'Environment', label: 'ENVIRONMENT' },
  { value: 'Human Resources', label: 'HUMAN RESOURCES' },
  { value: 'Law Enforcement', label: 'LAW ENFORCEMENT' },
  { value: 'Mental Health', label: 'MENTAL HEALTH' },
  { value: 'Public Administration', label: 'PUBLIC ADMINISTRATION' },
  { value: 'Public Health', label: 'PUBLIC HEALTH' },
  { value: 'Public Safety', label: 'PUBLIC SAFETY' },
  { value: 'Recreation', label: 'RECREATION' },
  { value: 'Social Justice', label: 'SOCIAL JUSTICE' },
  { value: 'Technology', label: 'TECHNOLOGY' },
  { value: 'Transit', label: 'TRANSIT' },
  { value: 'Urban Planning', label: 'URBAN PLANNING' },
];

const isOpenItem = item => item?.closed === false
	|| (typeof item?.closed === 'string' && item.closed.trim().toLowerCase() === 'false');

	function FocusAreaModal({ data, onApply, onClose, currentValue, onResetAll, onBack }) {
			const { isClosing, requestClose } = useAnimatedModalClose(onClose);
		const filterName = 'Focus Area';
	const [selected, setSelected] = useState(currentValue || '');
	const modalRef = useRef(null);
	const closeRef = useRef(null);
	const { swipeOffset, isSwiping, swipedDownRef } = useModalTouchSwipe({ modalRef, requestClose });

	useModalFocusTrap({
		modalRef,
		requestClose,
		initialFocusRef: closeRef,
		initialFocusSelector: onBack ? '.edu-modal-back' : undefined,
	});

	const resultCount = useMemo(() => {
		if (!selected) return 0;
		return (data || []).filter(item => isOpenItem(item) && (item.focusAreas || []).some(area => area === selected)).length;
	}, [data, selected]);
	const applyLabel = selected ? `VIEW RESULTS (${resultCount})` : 'VIEW RESULTS';

	const handleApply = () => {
		onApply(selected);
		requestClose();
	};

	const handleBack = () => {
		requestClose(onBack);
	};

		const handleReset = () => {
			setSelected('');
			if (onResetAll) onResetAll();
		};

	const openedFromTrigger = !onBack;
	const stackedAnimClass = onBack && !isClosing ? ' stacked-slide-in'
		: (isClosing && onBack && !swipedDownRef.current ? ' stacked-slide-out' : '');
	const slowSlideClass = (openedFromTrigger || (isClosing && swipedDownRef.current))
		? (isClosing ? ' slow-slide-down' : ' slow-slide-up')
		: '';
	return (
		<div
			className={`edu-modal-overlay${isClosing ? ' closing' : ''}`}
			role="presentation"
			onClick={e => { if (e.target === e.currentTarget) requestClose(); }}
		>
			<div
				className={`edu-modal${isClosing ? ' closing' : ''}${stackedAnimClass}${slowSlideClass}${isSwiping ? ' is-swiping' : ''}`}
				role="dialog"
				aria-modal="true"
				aria-labelledby="focus-modal-title"
				ref={modalRef}
				style={{
					'--swipe-offset': `${swipeOffset}px`,
					'--swipe-opacity': swipeOffset > 0 ? String(Math.max(0.25, 1 - swipeOffset / 420)) : '1',
				}}
			>
				<button className="edu-modal-handle-btn" onClick={requestClose} aria-label="Close modal" type="button" />
				<div
					className="edu-modal-header"
				>
					{onBack ? (
						<button
							className="edu-modal-back"
							onClick={handleBack}
							aria-label="Back to all filters"
							type="button"
						>
							<span aria-hidden="true">&lt;</span>
						</button>
					) : <span className="edu-modal-back-spacer" aria-hidden="true" />}
					<h2 id="focus-modal-title" className="edu-modal-title">Choose {filterName}</h2>
					<button
						className="edu-modal-close"
						onClick={requestClose}
						aria-label="Close focus area filter"
						ref={closeRef}
						type="button"
					>
						<span aria-hidden="true">&#10005;</span>
					</button>
				</div>

				<fieldset className="edu-modal-fieldset">
					<legend className="sr-only">Focus area</legend>
					{FOCUS_OPTIONS.map(opt => (
						<label key={opt.value} className="edu-modal-option">
							<span className="edu-modal-option-label">{opt.label}</span>
							<input
								type="radio"
								name="focus-area"
								value={opt.value}
								checked={selected === opt.value}
								onChange={() => { setSelected(opt.value); }}
							/>
						</label>
					))}
				</fieldset>

				<div className="edu-modal-actions">
					<button
						className="edu-modal-apply"
						onClick={handleApply}
						disabled={!selected}
					>
						{applyLabel}
					</button>
					<button className="edu-modal-reset" onClick={handleReset}>
						CLEAR FILTERS
					</button>
				</div>
			</div>
		</div>
	);
}

export default FocusAreaModal;
